import React, { useEffect, useState } from 'react';

/**
 * Renders a JPEG poster from the first decodable video frame (works for .mov / quicktime when the browser can decode).
 */
export default function VideoFrameThumbnail({ src, className = '', onFail }) {
  const [posterUrl, setPosterUrl] = useState(null);

  useEffect(() => {
    if (!src) {
      onFail?.();
      return undefined;
    }

    let cancelled = false;
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    const dispose = () => {
      video.pause();
      video.removeAttribute('src');
      video.load();
    };

    const fail = () => {
      if (!cancelled) onFail?.();
      dispose();
    };

    const captureFrame = () => {
      if (cancelled) return;
      const w = video.videoWidth;
      const h = video.videoHeight;
      if (!w || !h) {
        fail();
        return;
      }
      const canvas = document.createElement('canvas');
      const maxEdge = 480;
      const scale = Math.min(1, maxEdge / Math.max(w, h));
      canvas.width = Math.max(1, Math.round(w * scale));
      canvas.height = Math.max(1, Math.round(h * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        fail();
        return;
      }
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        setPosterUrl(canvas.toDataURL('image/jpeg', 0.86));
      } catch {
        fail();
        return;
      }
      dispose();
    };

    const seekToPosterTime = () => {
      if (cancelled) return;
      const duration = video.duration;
      const t =
        Number.isFinite(duration) && duration > 0 ? Math.min(0.35, Math.max(0.05, duration * 0.03)) : 0.1;

      const onSeeked = () => {
        video.removeEventListener('seeked', onSeeked);
        captureFrame();
      };
      video.addEventListener('seeked', onSeeked);

      try {
        video.currentTime = t;
      } catch {
        captureFrame();
      }
    };

    video.addEventListener('error', fail);
    video.addEventListener('loadeddata', seekToPosterTime);
    video.src = src;

    return () => {
      cancelled = true;
      video.removeEventListener('error', fail);
      video.removeEventListener('loadeddata', seekToPosterTime);
      dispose();
    };
  }, [src, onFail]);

  if (posterUrl) {
    return <img src={posterUrl} alt="" className={className} decoding="async" />;
  }

  return <div className={`sb-drive-thumb__video-pending ${className}`.trim()} aria-hidden />;
}
